/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  Box,
  Flex,
  VStack,
  Text,
  Avatar,
  Center,
  Button,
  HStack,
  IconButton,
  Input,
  Spinner,
  ListItem,
  UnorderedList,
  OrderedList,
  Link,
} from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppCard from 'presentation/components/shared/card/AppCard';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import { Color } from 'presentation/theme/colorScheme';
import AiChatSidebarList from './AiChatSidebarList';
import { useChat } from './ChatContext';
import { useFetchAiMessageListQuery, useSendAiMessageMutation, useFetchAiChatListQuery } from 'rtkQuery/sharespro/queries/chat';
import type { AiMessage, PerplexityMessage } from 'model/modules/chatAi/chatAi';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { useSendPerplexityMessageMutation } from 'rtkQuery/sharespro/queries/perplexity';
import ReactMarkdown from 'react-markdown';
const filterAlternatingMessages = (messages: PerplexityMessage[]): PerplexityMessage[] => {
  if (messages.length === 0) return [];

  const result: PerplexityMessage[] = [];
  let expectUserMessage = true;

  for (const msg of [...messages].reverse()) {
    const isUserMessage = msg.role === 'user';

    if (isUserMessage === expectUserMessage) {
      if (result.length > 0 && result[result.length - 1].role === msg.role) {
        result.pop();
      }
      result.push(msg);
      expectUserMessage = !expectUserMessage;
    } else if (isUserMessage && result.length > 0 && result[result.length - 1].role === msg.role) {
      result.pop();
      result.push(msg);
    }
  }

  return result;
};

const cleanBracketedReferences = (text: string): string => {
  return text.replace(/\[\d+\]/g, '');
};

const formatMathFormula = (text: string): string => {
  return text
    .replace(/\\\[|\\\]/g, '')
    .replace(/\\;/g, ' ')
    .replace(/\\,/g, '')
    .replace(/\\times/g, '×')
    .replace(/\\div/g, '÷')
    .replace(/\\pm/g, '±')
    .replace(/\\approx/g, '≈')
    .replace(/\\leq/g, '≤')
    .replace(/\\geq/g, '≥')
    .replace(/\\neq/g, '≠')
    .replace(/\\infty/g, '∞')
    .replace(/\\sum/g, '∑')
    .replace(/\\prod/g, '∏')
    .replace(/\\sqrt/g, '√')
    .replace(/\\pi/g, 'π')
    .replace(/\\alpha/g, 'α')
    .replace(/\\beta/g, 'β')
    .replace(/\\gamma/g, 'γ')
    .replace(/\\delta/g, 'δ')
    .replace(/\\theta/g, 'θ')
    .replace(/\\lambda/g, 'λ')
    .replace(/\\mu/g, 'μ')
    .replace(/\\sigma/g, 'σ')
    .replace(/\\omega/g, 'ω')
    .replace(/\\Omega/g, 'Ω')
    .replace(/\\phi/g, 'φ')
    .replace(/\\Phi/g, 'Φ')
    .replace(/\\psi/g, 'ψ')
    .replace(/\\Psi/g, 'Ψ')
    .replace(/\\xi/g, 'ξ')
    .replace(/\\Xi/g, 'Ξ')
    .replace(/\\zeta/g, 'ζ')
    .replace(/\\eta/g, 'η')
    .replace(/\\rho/g, 'ρ')
    .replace(/\\tau/g, 'τ')
    .replace(/\\upsilon/g, 'υ')
    .replace(/\\Upsilon/g, 'Υ')
    .replace(/\\chi/g, 'χ')
    .replace(/\\Chi/g, 'Χ')
    .replace(/\\iota/g, 'ι')
    .replace(/\\Iota/g, 'Ι')
    .replace(/\\kappa/g, 'κ')
    .replace(/\\Kappa/g, 'Κ')
    .replace(/\\nu/g, 'ν')
    .replace(/\\Nu/g, 'Ν')
    .replace(/\\omicron/g, 'ο')
    .replace(/\\Omicron/g, 'Ο')
    .replace(/\\rho/g, 'ρ')
    .replace(/\\Rho/g, 'Ρ')
    .replace(/\\sigma/g, 'σ')
    .replace(/\\Sigma/g, 'Σ')
    .replace(/\\tau/g, 'τ')
    .replace(/\\Tau/g, 'Τ')
    .replace(/\\upsilon/g, 'υ')
    .replace(/\\Upsilon/g, 'Υ')
    .replace(/\\phi/g, 'φ')
    .replace(/\\Phi/g, 'Φ')
    .replace(/\\chi/g, 'χ')
    .replace(/\\Chi/g, 'Χ')
    .replace(/\\psi/g, 'ψ')
    .replace(/\\Psi/g, 'Ψ')
    .replace(/\\omega/g, 'ω')
    .replace(/\\Omega/g, 'Ω');
};
const preprocessText = (text: string): string => {
  // Сначала обрабатываем ссылки в формате "текст(ссылка)"
  let result = text.replace(/([^()\s]+)\(((?:https?|ftp):\/\/[^\s)]+)\)/g, '[$1]($2)');

  // Затем обрабатываем обычные URL
  result = result.replace(/((?:https?|ftp):\/\/[^\s)]+)/g, (url) => {
    return `[${url}](${url})`;
  });

  // Удаляем ссылки на источники в квадратных скобках
  result = result.replace(/\[\d+\]/g, '');

  return result;
};

const MessageContent = ({ text }: { text: string }): JSX.Element => {
  const formattedText = formatMathFormula(cleanBracketedReferences(preprocessText(text)));

  return (
    <Box>
      <ReactMarkdown
        components={{
          ul: ({ children }) => (
            <UnorderedList pl={6} mb={2}>
              {children}
            </UnorderedList>
          ),
          ol: ({ children }) => (
            <OrderedList pl={6} mb={2}>
              {children}
            </OrderedList>
          ),
          li: ({ children }) => (
            <ListItem mb={1}>
              {children}
            </ListItem>
          ),
          p: ({ children }) => (
            <Text mb={2}>{children}</Text>
          ),
          strong: ({ children }) => (
            <Text as="span" fontWeight="bold">{children}</Text>
          ),
          a: ({ href, children }) => (
            <Link
              as="a"
              href={`https://${children}`}
              isExternal
              color="blue.500"
              textDecoration="underline"
              _hover={{ textDecoration: 'none' }}
            >
              {children}
            </Link>
          ),
        }}
      >
        {formattedText}
      </ReactMarkdown>
    </Box>
  );
};

const AiChatView = (): JSX.Element => {
  const { activeChat } = useChat();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [allMessages, setAllMessages] = useState<AiMessage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isThinking, setIsThinking] = useState(false);
  const scrollBottomRef = useRef<HTMLDivElement>(null);
  const activeChatId = activeChat?.id;
  const latestChatId = useRef<string | null>(null);
  const { refetch } = useFetchAiChatListQuery();

  useEffect(() => {
    if (activeChat?.id !== latestChatId.current) {
      setPage(1);
      setAllMessages([]);
      setHasMore(false);
      latestChatId.current = activeChat?.id ?? null;
    }
  }, [activeChat?.id]);

  const { data, isFetching, isSuccess } = useFetchAiMessageListQuery(
    { externalId: activeChat?.id ?? '', page },
    { skip: !activeChat }
  );

  useEffect(() => {
    if (
      isSuccess &&
      data?.items?.length &&
      activeChat?.id === latestChatId.current
    ) {
      if (page === 1) {
        setAllMessages(data.items);
      } else {
        const newIds = new Set(allMessages.map((m) => m.id));
        const uniqueOldFirst = data.items.filter((m) => !newIds.has(m.id));
        setAllMessages((prev) => [...prev, ...uniqueOldFirst]);
      }
      setHasMore(data.currentPage < data.pageCount);
    }
  }, [data, isSuccess, activeChat?.id, page]);

  useEffect(() => {
    if (page === 1 && allMessages.length > 0) {
      setTimeout(() => {
        // scrollBottomRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [allMessages, page]);
  const [input, setInput] = useState('');
const [sendAiMessage] = useSendAiMessageMutation();
const [sendPerplexityMessage] = useSendPerplexityMessageMutation();
const handleSend = async () => {
  const trimmed = input.trim();
  if (!trimmed || !activeChatId) return;

  const userMessage: AiMessage = {
    id: uuidv4(),
    createdAt: new Date(),
    text: trimmed,
    isFromAi: false,
  };

  setAllMessages((prev) => [userMessage, ...prev]);
  setInput('');
  setIsThinking(true);

  try {
    await sendAiMessage({ externalId: activeChatId, messages: [userMessage] });

    const messagesForPerplexity: PerplexityMessage[] = [...allMessages, userMessage]
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      .map(msg => ({
        role: msg.isFromAi ? 'assistant' : 'user' as const,
        content: msg.text,
      }));

    const alternatingMessages = filterAlternatingMessages(messagesForPerplexity.reverse());

    const assistantText = await sendPerplexityMessage(alternatingMessages).unwrap();

    const assistantMessage: AiMessage = {
      id: uuidv4(),
      createdAt: new Date(),
      text: formatMathFormula(cleanBracketedReferences(assistantText)),
      isFromAi: true,
    };

    setAllMessages((prev) => [assistantMessage, ...prev]);

    await sendAiMessage({ externalId: activeChatId, messages: [assistantMessage] });
    await refetch();
  } catch (error) {
    console.error('Ошибка при отправке сообщения:', error);
  } finally {
    setIsThinking(false);
  }
};

  return (
    <AiChatSidebarList>
      <GridHeading>{t('ai')}</GridHeading>
      <AppCard w="100%" h="75vh" p="40px">
        {activeChat?.id ? (
          <Flex direction="column" h="100%" gap={3}>
            <Box flex="1" overflowY="auto" display="flex" flexDirection="column-reverse">
              <VStack spacing={3} align="stretch" mt="auto">
                {hasMore && (
                  <Center py={4}>
                    <Button
                      onClick={() => setPage((prev) => prev + 1)}
                      isLoading={isFetching}
                      size="sm"
                    >
                      {t('loadMore')}
                    </Button>
                  </Center>
                )}
                {allMessages
                  .slice()
                  .reverse()
                  .map((msg) => (
                    <Flex
                      key={`${msg.id}-${msg.createdAt.getTime()}`}
                      align="flex-start"
                      justify={msg.isFromAi ? 'flex-start' : 'flex-end'}
                      p={2}
                    >
                      {msg.isFromAi && (
                        <Avatar size="sm" name="AI" src="/avatar.png" mr={2} />
                      )}
                      <Box
                        maxW="70%"
                        bg={msg.isFromAi ? 'gray.100' : Color.Primary200}
                        p={4}
                        borderRadius="lg"
                        color="black"
                      >
                        <Box>
                          <MessageContent text={msg.text} />
                        </Box>
                      </Box>
                    </Flex>
                  ))}
                {isThinking && (
                  <Flex align="flex-start" p={2}>
                    <Avatar size="sm" name="AI" src="/avatar.png" mr={2} />
                    <Box maxW="70%" bg="gray.100" p={3} borderRadius="lg" color="black">
                      <HStack spacing={2}>
                        <Spinner size="sm" />
                      </HStack>
                    </Box>
                  </Flex>
                )}
                <div ref={scrollBottomRef} />
              </VStack>
            </Box>
            <HStack mt={3}>
              <Input
                placeholder={t('enterMessage')!}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSend();
                }}
              />
              <IconButton
                icon={<ArrowForwardIcon />}
                colorScheme="green"
                onClick={handleSend}
                aria-label="Send"
                isDisabled={!input.trim() || isThinking}
              />
            </HStack>
          </Flex>
        ) : (
          <Center h="75vh">
            <Text color="gray.500">{t('selectChatFromList')}</Text>
          </Center>
        )}
      </AppCard>
    </AiChatSidebarList>
  );
};

export default AiChatView;
